// src/App.jsx
import { useState, useMemo, useEffect, useCallback, lazy, Suspense } from "react";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { identifyUser, resetUser, trackUpgradeClicked } from "./lib/analytics";
import { useTabTracking } from "./hooks/useAnalytics";
import "./index.css";

import { usePersistence }  from "./hooks/usePersistence";
import { usePortfolio }    from "./hooks/usePortfolio";
import { useMarketData }   from "./hooks/useMarketData";
import { useStockData }    from "./hooks/useStockData";
import { STOCK_UNIVERSE }  from "./data/stocks";

import Header           from "./components/Header";
import LiveDataBanner   from "./components/LiveDataBanner";
import SyncBadge        from "./components/SyncBadge";
import Dashboard        from "./components/Dashboard";
import Onboarding       from "./components/Onboarding";
import { useUserPreferences } from "./hooks/useUserPreferences";
import { useAuth }      from "./hooks/useAuth";
import { useGeoEventBus } from "./hooks/useGeoEventBus";
import { useForexPositions } from "./hooks/useForexPositions";
import { getOnboardingState, completeOnboarding } from "./lib/onboarding/onboardingService";
import { onboardingGuard } from "./lib/onboarding/onboardingGuard";
import { hasSupabase } from "./lib/supabaseClient";
import { saveSnapshot } from "./lib/portfolioSnapshots";
import { purgeDeprecatedLocalStorage } from "./lib/user/purgeDeprecatedLocalStorage";
import HomePage         from "./components/HomePage";
import LoginPage        from "./components/LoginPage";
import SettingsDrawer   from "./components/SettingsDrawer";
const BillingPage       = lazy(() => import("./components/BillingPage"));
import TermsAcceptanceOverlay from "./components/TermsAcceptanceOverlay";
import TermsOfService         from "./components/TermsOfService";
import PrivacyPolicy          from "./components/PrivacyPolicy";
import CookieNotice           from "./components/CookieNotice";
import LoadingScreen          from "./components/LoadingScreen";
import ModuleErrorBoundary    from "./components/ModuleErrorBoundary";
import BetaGate              from "./components/BetaGate";
import OnboardingGuide        from "./components/OnboardingGuide";
import FeedbackButton         from "./components/FeedbackButton";
import AdminDashboard         from "./components/AdminDashboard";

// ── Lazy-loaded modules (code-split for faster initial load) ──
const PortfolioBuilder  = lazy(() => import("./components/PortfolioBuilder"));
const ValuationScreen   = lazy(() => import("./components/ValuationScreen"));
const Optimizer         = lazy(() => import("./components/Optimizer"));
const MonteCarloTab     = lazy(() => import("./components/MonteCarlo"));
const AnalyticsTab      = lazy(() => import("./components/Analytics"));
const BacktestTab       = lazy(() => import("./components/Backtest"));
const DCFTab            = lazy(() => import("./components/DCF"));
const WorldMonitor      = lazy(() => import("./components/WorldMonitor"));
const NewsSentiment     = lazy(() => import("./components/NewsSentiment"));
const MarketWatch       = lazy(() => import("./components/MarketWatch"));
const MacroPanel        = lazy(() => import("./components/MacroPanel"));
const FXCryptoPanel     = lazy(() => import("./components/FXCryptoPanel"));
const CommoditiesPanel  = lazy(() => import("./components/CommoditiesPanel"));
const Screener          = lazy(() => import("./components/Screener"));
const WEIPanel          = lazy(() => import("./components/WEIPanel"));
const WBPanel           = lazy(() => import("./components/WBPanel"));
const WCRSPanel         = lazy(() => import("./components/WCRSPanel"));
const FIRSPanel         = lazy(() => import("./components/FIRSPanel"));
const RVPanel           = lazy(() => import("./components/RVPanel"));
const FEDPanel          = lazy(() => import("./components/FEDPanel"));
const CACSPanel         = lazy(() => import("./components/CACSPanel"));
const ECSTPanel         = lazy(() => import("./components/ECSTPanel"));
const FixedIncomePanel  = lazy(() => import("./components/FixedIncomePanel"));
const FXMarketsPanel    = lazy(() => import("./components/FXMarketsPanel"));
const TerminalMode      = lazy(() => import("./components/TerminalMode"));
const WorkspaceMode     = lazy(() => import("./components/WorkspaceMode"));
const InsiderIntelligence = lazy(() => import("./components/InsiderIntelligence"));
const ETFScreener         = lazy(() => import("./components/ETFScreener"));
const FuturesPanel        = lazy(() => import("./components/FuturesPanel"));

export default function App() {
  // ── URL-based navigation ────────────────────────────────────────────────────
  const navigate  = useNavigate();
  const location  = useLocation();
  const tabMatch  = useMatch("/app/:tab");

  // Active tab is derived from the URL — /app/dashboard → "dashboard"
  const activeTab = tabMatch?.params?.tab ?? "dashboard";
  const setActiveTab = useCallback((tab) => navigate(`/app/${tab}`), [navigate]);

  // ── Auth (must be first — provides user for usePersistence) ──────────────
  const {
    user, session, authLoading, authReady, authError,
    clearError, signIn, signUp, signInWithGoogle, signOut,
    tier, setTier, refreshTier,
    termsAccepted, acceptTerms,
    isAdmin,
    resetPassword, updatePassword, isPasswordRecovery,
    hasSupabase: supabaseAvailable,
  } = useAuth();

  const [settingsOpen, setSettingsOpen] = useState(false);

  // Redirect logged-in users away from landing/login screens
  useEffect(() => {
    if (!authLoading && user && !isPasswordRecovery) {
      const p = location.pathname;
      if (p === "/" || p === "/login") {
        navigate("/app/dashboard", { replace: true });
      }
      if (p === "/app") {
        navigate("/app/dashboard", { replace: true });
      }
    }
  }, [user, authLoading, isPasswordRecovery, location.pathname]); // eslint-disable-line

  // Open billing if returning from a Stripe 3DS redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("payment_intent_client_secret")) {
      navigate("/billing", { replace: true });
    }
  }, []); // eslint-disable-line

  // Remove localStorage keys that have been migrated to Supabase (one-time on mount)
  useEffect(() => { purgeDeprecatedLocalStorage(); }, []);

  const handleUpgradeSuccess = useCallback(() => {
    setTier("pro");
    setTimeout(refreshTier, 3000);
  }, [setTier, refreshTier]);

  const handleSignOut = useCallback(() => {
    resetUser();
    signOut();
  }, [signOut]);

  // DB-backed settings — loads from Supabase user_preferences.
  // Gate on authReady (first onAuthStateChange event) + session non-null so the
  // fetch starts as soon as Supabase confirms the session, without waiting for
  // the slower ensure*/fetchProfile calls that keep authLoading=true.
  const {
    settings, setSetting, resetDefaults, loading: prefsLoading, isRetrying: prefsRetrying,
  } = useUserPreferences(user?.id, authReady && !!session);

  // Re-apply saved accent color on mount and on change
  useEffect(() => {
    document.documentElement.style.setProperty("--accent", settings.accentColor);
  }, [settings.accentColor]);

  // Apply font size scale (S/M/L) as CSS custom properties
  useEffect(() => {
    const scale = settings.fontSize === "S" ? 0.875 : settings.fontSize === "L" ? 1.125 : 1;
    document.documentElement.style.setProperty("--text-xs",   `${Math.round(11 * scale)}px`);
    document.documentElement.style.setProperty("--text-sm",   `${Math.round(13 * scale)}px`);
    document.documentElement.style.setProperty("--text-base", `${Math.round(15 * scale)}px`);
    document.documentElement.style.setProperty("--text-md",   `${Math.round(17 * scale)}px`);
    document.documentElement.style.setProperty("--text-lg",   `${Math.round(20 * scale)}px`);
  }, [settings.fontSize]);

  useTabTracking(activeTab);

  // ── Onboarding guard — DB-backed, never localStorage ──────────────────────
  const [onboardingStatus, setOnboardingStatus] = useState("loading");

  const userId = user?.id ?? null;
  useEffect(() => {
    // Gate on authReady (first onAuthStateChange event) + session non-null.
    // authReady ensures Supabase has confirmed the session before we query —
    // no timeout needed; if Supabase responds slowly, we wait it out while
    // the LoadingScreen is visible.
    if (!userId || !authReady || !session) {
      setOnboardingStatus("loading");
      return;
    }
    if (!hasSupabase) {
      setOnboardingStatus(
        localStorage.getItem("onboarding_complete") ? "complete" : "pending"
      );
      return;
    }
    let cancelled = false;
    let retryTimer = null;
    const RETRY_DELAYS = [5000, 10000, 20000, 30000];

    const MAX_RETRY_ATTEMPTS = 8; // ~3.5 minutes total before giving up

    function scheduleOnboardingRetry(attempt) {
      if (cancelled) return;
      if (attempt >= MAX_RETRY_ATTEMPTS) return;
      const delay = RETRY_DELAYS[Math.min(attempt, RETRY_DELAYS.length - 1)];
      retryTimer = setTimeout(async () => {
        if (cancelled) return;
        try {
          const state = await getOnboardingState(userId);
          if (cancelled) return;
          setOnboardingStatus(state?.status ?? "complete");
        } catch {
          scheduleOnboardingRetry(attempt + 1);
        }
      }, delay);
    }

    setOnboardingStatus("loading");
    const onboardingTimeout = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('onboarding fetch timed out')), 30000)
    );
    Promise.race([getOnboardingState(userId), onboardingTimeout]).then((state) => {
      if (cancelled) return;
      setOnboardingStatus(state?.status ?? "complete");
    }).catch(() => {
      if (cancelled) return;
      console.warn("[Nuvport] onboardingState timed out — defaulting to complete, retrying in background");
      setOnboardingStatus("complete");
      scheduleOnboardingRetry(0);
    });
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [userId, authReady, session]);

  // ── Persistence (Supabase + localStorage) ──────────────
  const {
    holdings:         persistedHoldings,
    riskTolerance:    persistedRisk,
    watchlist:        persistedWatchlist,
    syncStatus,
    setHoldings:      setPersistedHoldings,
    setRiskTolerance: setPersistedRisk,
    setWatchlist:     setPersistedWatchlist,
    hasSupabase,
    clearUserData,
  } = usePersistence(user?.id, tier);

  // ── Portfolio logic wired to persistence setters ───────
  const {
    holdings, totalWeight, metrics, sectorBreakdown,
    riskTolerance, setRiskTolerance,
    addStock, removeStock, updateWeight, equalWeight,
    optimize, addCustomStock, importHoldings,
  } = usePortfolio({
    holdings:         persistedHoldings,
    setHoldings:      setPersistedHoldings,
    riskTolerance:    persistedRisk,
    setRiskTolerance: setPersistedRisk,
    tier,
    onOpenBilling:    () => navigate("/billing"),
  });

  // ── Market data enrichment ─────────────────────────────
  // Gated behind authReady — nothing fetches until onAuthStateChange confirms
  // the session. Without this gate, holdings pre-loaded from localStorage would
  // trigger Supabase alpha_overview_cache queries under the anon role (before auth),
  // causing RLS-blocked 0-row responses that produce 406 errors from .maybeSingle().
  const holdingsForMarket = useMemo(
    () => (authReady ? holdings : []),
    [authReady, holdings.map(h => `${h.ticker}:${h.weight}`).join(",")]  // eslint-disable-line
  );
  const {
    enrichedHoldings, loading: dataLoading,
    errors: dataErrors, lastFetch, hasApiKey, refresh,
  } = useMarketData(holdingsForMarket);

  // ── Watchlist ──────────────────────────────────────────
  const watchlistTickers    = persistedWatchlist;
  const setWatchlistTickers = setPersistedWatchlist;

  // ── Forex positions (localStorage-backed, hoisted for Dashboard + FXMarkets) ─
  const { positions: forexPositions, addPosition: addForexPosition, removePosition: removeForexPosition }
    = useForexPositions();

  // ── Smart tab-aware polling ────────────────────────────
  // Convert forex pair "EUR/USD" → "EURUSD=X" for Yahoo Finance compatibility
  const forexYahooTickers = useMemo(
    () => forexPositions.map(p => p.pair.replace("/", "") + "=X"),
    [forexPositions]
  );

  const allTickers = useMemo(() => {
    const portfolioTickers = holdings.map(h => h.ticker);
    const universeTickers  = STOCK_UNIVERSE.map(s => s.ticker);
    if (["marketwatch","macro","fx","commodities","fxmarkets"].includes(activeTab))
      return [...new Set([...portfolioTickers, ...watchlistTickers, ...forexYahooTickers])];
    // tabs that fetch their own historical/non-equity data — no live polling needed
    if (["backtest","montecarlo","world","news","wei","wb","wcrs","firs","fed","ecst","cacs","fixedincome","insider"].includes(activeTab))
      return [];
    // futures tab: poll open forex positions for live P&L
    if (activeTab === "futures")
      return forexYahooTickers;
    if (activeTab === "valuation" || activeTab === "screener" || activeTab === "rv")
      return [...new Set([...portfolioTickers, ...universeTickers, ...forexYahooTickers])];
    return [...new Set([...portfolioTickers, ...forexYahooTickers])];
  }, [holdings, watchlistTickers, activeTab, forexYahooTickers]);

  const pollInterval = (activeTab === "valuation" || activeTab === "screener" || activeTab === "rv")
    ? 30000
    : (settings.pollIntervalMs ?? 2000);

  const stockData = useStockData(allTickers, pollInterval);

  // ── Merge live quotes onto enriched holdings ───────────
  const liveHoldings = useMemo(() => {
    return enrichedHoldings.map(h => {
      const q = stockData.getQuote(h.ticker);
      if (!q) return h;
      return {
        ...h,
        livePrice: q.price, liveChange: q.change, liveChangePct: q.changePct,
        livePositive: q.positive, liveName: q.name,
        liveMarketCap: q.marketCap, liveVolume: q.volume,
        sparkline: q.sparkline ?? h.sparkline ?? [],
        price: q.price ?? h.price,
      };
    });
  // eslint-disable-next-line
  }, [enrichedHoldings, stockData.quotes]);

  const geoEventBus = useGeoEventBus({ portfolio: enrichedHoldings });

  // ── Analytics — identify user when auth state is known ────────────────────
  useEffect(() => {
    if (user) {
      identifyUser(user.id, {
        tier,
        createdAt: user.created_at,
        hasPortfolio: holdings.length > 0,
        holdingCount: holdings.length,
      });
    }
  }, [user?.id, tier, holdings.length]); // eslint-disable-line

  // ── Legal pages — accessible from anywhere via URL ────────────────────────
  if (location.pathname === "/terms") {
    return <TermsOfService onBack={() => navigate(-1)} />;
  }
  if (location.pathname === "/privacy") {
    return <PrivacyPolicy onBack={() => navigate(-1)} />;
  }

  // ── Auth gate ──────────────────────────────────────────────────────────────
  if (authLoading || (user && prefsLoading)) {
    return <LoadingScreen />;
  }

  if (!user || isPasswordRecovery) {
    if (location.pathname === "/login" || isPasswordRecovery) {
      return (
        <>
          <LoginPage
            onBack={() => { navigate("/"); clearError(); }}
            signIn={signIn}
            signUp={signUp}
            signInWithGoogle={signInWithGoogle}
            authError={authError}
            clearError={clearError}
            hasSupabase={supabaseAvailable}
            onOpenTerms={() => navigate("/terms")}
            onOpenPrivacy={() => navigate("/privacy")}
            resetPassword={resetPassword}
            updatePassword={updatePassword}
            isPasswordRecovery={isPasswordRecovery}
          />
          <CookieNotice onOpenPrivacy={() => navigate("/privacy")} />
        </>
      );
    }
    if (location.pathname === "/access") {
      return (
        <BetaGate
          onCodeAccepted={() => navigate("/login")}
          onSignIn={() => navigate("/login")}
        />
      );
    }
    return (
      <>
        <HomePage
          onLogin={() => navigate("/access")}
          onOpenTerms={() => navigate("/terms")}
          onOpenPrivacy={() => navigate("/privacy")}
        />
        <CookieNotice onOpenPrivacy={() => navigate("/privacy")} />
      </>
    );
  }

  // ── Terms gate ────────────────────────────────────────────────────────────
  if (user && termsAccepted === false) {
    return (
      <div style={{ position: "fixed", inset: 0, background: "var(--bg)" }}>
        <TermsAcceptanceOverlay
          onOpenTerms={() => navigate("/terms")}
          onOpenPrivacy={() => navigate("/privacy")}
          onAccept={acceptTerms}
        />
      </div>
    );
  }

  // ── Onboarding guard gate ─────────────────────────────────────────────────
  const guardResult = onboardingGuard(onboardingStatus);

  if (guardResult === "loading") {
    return <LoadingScreen />;
  }

  if (guardResult === "redirect") {
    return (
      <Onboarding
        userId={user.id}
        onComplete={async (profile) => {
          // Retry DB write up to 3× — a silent failure leaves the row as
          // "pending" and re-triggers onboarding on next login.
          let written = false;
          for (let attempt = 1; attempt <= 3 && !written; attempt++) {
            try {
              await completeOnboarding(user.id);
              written = true;
            } catch (err) {
              console.warn(`[Nuvport] completeOnboarding attempt ${attempt} failed:`, err?.message);
              if (attempt < 3) await new Promise(r => setTimeout(r, 600 * attempt));
            }
          }
          if (!written) console.warn("[Nuvport] completeOnboarding: all retries failed — onboarding will re-trigger on next login");

          // Save all profile fields collected during onboarding via the single
          // setSetting path. portfolioValue goes here too — avoids the race
          // condition where usePortfolioValue wrote to DB but useUserPreferences
          // already loaded with null and React state never got updated.
          setSetting("welcomeGuideDone", true);
          setSetting("profileSetupDone", true);
          if (profile?.displayName)                setSetting("displayName", profile.displayName);
          if (profile?.benchmark)                  setSetting("benchmark", profile.benchmark);
          if (profile?.alerts)                     setSetting("alerts", profile.alerts);
          if (profile?.bio)                        setSetting("bio", profile.bio);
          if (profile?.country)                    setSetting("country", profile.country);
          if (profile?.goals?.length)              setSetting("goals", profile.goals);
          if (profile?.riskTolerance !== undefined) setRiskTolerance(profile.riskTolerance);
          if (profile?.portfolioValue != null) {
            setSetting("portfolioValue", profile.portfolioValue);
            // Fire-and-forget snapshot — for portfolio value history chart.
            // Non-critical: failure is silent, won't block navigation.
            saveSnapshot(user.id, profile.portfolioValue, "onboarding").catch(() => {});
          }

          setOnboardingStatus("complete");
          navigate("/app/dashboard", { replace: true });
        }}
        addStock={addStock}
        currentPortfolioValue={settings.portfolioValue}
      />
    );
  }

  // ── Billing page ──────────────────────────────────────────────────────────
  if (location.pathname === "/billing") {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <BillingPage
          tier={tier}
          onBack={() => navigate(-1)}
          onTierChange={handleUpgradeSuccess}
        />
      </Suspense>
    );
  }

  // ── Workspace mode ───────────────────────────────────────────────────────
  if (settings.uiMode === "workspace") {
    return (
      <WorkspaceMode
        settings={settings}
        setSetting={setSetting}
        resetDefaults={resetDefaults}
        hasSupabase={hasSupabase}
        liveHoldings={liveHoldings}
        enrichedHoldings={enrichedHoldings}
        totalWeight={totalWeight}
        metrics={metrics}
        stockData={stockData}
        sectorBreakdown={sectorBreakdown}
        addStock={addStock}
        removeStock={removeStock}
        updateWeight={updateWeight}
        equalWeight={equalWeight}
        addCustomStock={addCustomStock}
        optimize={optimize}
        riskTolerance={riskTolerance}
        setRiskTolerance={setRiskTolerance}
        watchlistTickers={watchlistTickers}
        setWatchlistTickers={setWatchlistTickers}
        user={user}
        onSignOut={handleSignOut}
        tier={tier}
        onOpenBilling={() => navigate("/billing")}
        importHoldings={importHoldings}
        geoEventBus={geoEventBus}
      />
    );
  }

  // ── Terminal mode ─────────────────────────────────────────────────────────
  if (settings.uiMode === "terminal") {
    return (
      <TerminalMode
        settings={settings}
        setSetting={setSetting}
        resetDefaults={resetDefaults}
        hasSupabase={hasSupabase}
        liveHoldings={liveHoldings}
        enrichedHoldings={enrichedHoldings}
        totalWeight={totalWeight}
        metrics={metrics}
        stockData={stockData}
        sectorBreakdown={sectorBreakdown}
        addStock={addStock}
        removeStock={removeStock}
        updateWeight={updateWeight}
        equalWeight={equalWeight}
        addCustomStock={addCustomStock}
        optimize={optimize}
        riskTolerance={riskTolerance}
        setRiskTolerance={setRiskTolerance}
        watchlistTickers={watchlistTickers}
        setWatchlistTickers={setWatchlistTickers}
        tier={tier}
        onOpenBilling={() => navigate("/billing")}
        importHoldings={importHoldings}
        geoEventBus={geoEventBus}
      />
    );
  }

  return (
    <div style={{ minHeight:"100vh", background: "var(--bg)" }}>

      {/* ── Onboarding Guide overlay — shown once after account setup ── */}
      {!settings.onboardingGuideDone && (
        <OnboardingGuide
          initialLanguageMode={settings.languageMode ?? 'plain'}
          onComplete={(chosenMode) => {
            setSetting('languageMode', chosenMode)
            setSetting('onboardingGuideDone', true)
          }}
        />
      )}

      <Header
        metrics={metrics}
        totalWeight={totalWeight}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onOpenSettings={() => setSettingsOpen(true)}
        onOpenBilling={() => navigate("/billing")}
        onToggleWorkspace={() => setSetting("uiMode", settings.uiMode === "workspace" ? "standard" : "workspace")}
        displayName={settings.displayName || ""}
        user={user}
        onSignOut={handleSignOut}
        tier={tier}
        isAdmin={isAdmin}
      />
      <SettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        setSetting={setSetting}
        resetDefaults={resetDefaults}
        holdings={liveHoldings}
        hasSupabase={hasSupabase}
        onSignOut={handleSignOut}
        onClearData={clearUserData}
        user={user}
        tier={tier}
        isRetrying={prefsRetrying}
      />

      {!settings.hideBanner && (
        <LiveDataBanner
          hasApiKey={hasApiKey}
          loading={dataLoading || stockData.loading}
          errors={dataErrors}
          lastFetch={stockData.lastUpdate ?? lastFetch}
          onRefresh={() => { refresh(); stockData.refresh?.(); }}
        />
      )}

      <SyncBadge status={syncStatus} hasSupabase={hasSupabase} />

      <Suspense fallback={<div style={{ padding: 40, textAlign: "center", color: "var(--text-secondary)", fontFamily: "var(--font-mono)", fontSize: 12, letterSpacing: "0.1em" }}>LOADING MODULE...</div>}>
      <ModuleErrorBoundary key={activeTab}>
      {activeTab === "world" ? (
        <WorldMonitor holdings={liveHoldings} settings={settings} geoEventBus={geoEventBus} onLayersChange={(layers) => setSetting("wmActiveLayers", layers)} />
      ) : (
        <main style={{ maxWidth:1320, margin:"0 auto", padding:"24px 24px 60px" }}>
          {activeTab === "dashboard"   && (
            <Dashboard
              holdings={liveHoldings}
              metrics={metrics}
              totalWeight={totalWeight}
              stockData={stockData}
              setActiveTab={setActiveTab}
              settings={settings}
              setSetting={setSetting}
              geoEventBus={geoEventBus}
              portfolioValue={settings.portfolioValue}
              user={user}
              tier={tier}
              onOpenBilling={() => navigate("/billing")}
              forexPositions={forexPositions}
            />
          )}
          {activeTab === "builder"     && (
            <PortfolioBuilder
              holdings={liveHoldings} totalWeight={totalWeight} metrics={metrics}
              addStock={addStock} removeStock={removeStock} updateWeight={updateWeight}
              equalWeight={equalWeight} addCustomStock={addCustomStock}
              onOptimize={() => { optimize(); setActiveTab("optimizer"); }}
              stockData={stockData}
              settings={settings}
              geoEventBus={geoEventBus}
              tier={tier}
              onOpenBilling={() => navigate("/billing")}
              importHoldings={importHoldings}
            />
          )}
          {activeTab === "valuation"   && <ValuationScreen holdings={liveHoldings} enrichedHoldings={enrichedHoldings} stockData={stockData} onAddToPortfolio={addStock} onRemoveFromPortfolio={removeStock} settings={settings} />}
          {activeTab === "optimizer"   && <Optimizer holdings={liveHoldings} metrics={metrics} riskTolerance={riskTolerance} setRiskTolerance={setRiskTolerance} optimize={optimize} settings={settings} />}
          {activeTab === "backtest"    && <BacktestTab holdings={liveHoldings} settings={settings} benchmark={settings.benchmark ?? "SPY"} />}
          {activeTab === "dcf"         && <DCFTab holdings={liveHoldings} stockData={stockData} settings={settings} />}
          {activeTab === "montecarlo"  && <MonteCarloTab metrics={metrics} liveHoldings={liveHoldings} settings={settings} geoEventBus={geoEventBus} portfolioValue={liveHoldings.reduce((sum, h) => { const p = h.livePrice ?? h.price; return p ? sum + p * (h.weight / 100) : sum; }, 0) || null} />}
          {activeTab === "charts"      && <AnalyticsTab holdings={liveHoldings} sectorBreakdown={sectorBreakdown} stockData={stockData} riskFreeRate={settings.riskFreeRate} settings={settings} />}
          {activeTab === "news"        && <NewsSentiment holdings={liveHoldings} settings={settings} stockData={stockData} tier={tier} onOpenBilling={() => navigate("/billing")} />}
          {activeTab === "screener"    && <Screener stockData={stockData} holdings={liveHoldings} onAddToPortfolio={addStock} onRemoveFromPortfolio={removeStock} settings={settings} />}
          {activeTab === "etfscreener" && <ETFScreener settings={settings} />}
          {activeTab === "wei"         && <WEIPanel />}
          {activeTab === "wb"          && <WBPanel />}
          {activeTab === "wcrs"        && <WCRSPanel />}
          {activeTab === "firs"        && <FIRSPanel />}
          {activeTab === "rv"          && <RVPanel stockData={stockData} holdings={liveHoldings} />}
          {activeTab === "fed"         && <FEDPanel />}
          {activeTab === "cacs"        && <CACSPanel holdings={liveHoldings} />}
          {activeTab === "ecst"        && <ECSTPanel />}
          {activeTab === "marketwatch" && (
            <MarketWatch
              holdings={liveHoldings} stockData={stockData}
              watchlistTickers={watchlistTickers} setWatchlistTickers={setWatchlistTickers}
              onAddToPortfolio={ticker => addStock(ticker)}
              onRemoveFromPortfolio={ticker => removeStock(ticker)}
              settings={settings}
            />
          )}
          {activeTab === "macro"       && <MacroPanel settings={settings} />}
          {activeTab === "fx"          && <FXCryptoPanel />}
          {activeTab === "commodities" && <CommoditiesPanel />}
          {activeTab === "fixedincome" && <FixedIncomePanel settings={settings} />}
          {activeTab === "fxmarkets"   && <FXMarketsPanel settings={settings} geoEventBus={geoEventBus} forexPositions={forexPositions} addForexPosition={addForexPosition} removeForexPosition={removeForexPosition} getQuote={stockData.getQuote} />}
          {activeTab === "futures"     && <FuturesPanel holdings={liveHoldings} settings={settings} />}
          {activeTab === "insider"     && <InsiderIntelligence tier={tier} onOpenBilling={() => navigate("/billing")} />}
          {activeTab === "admin"       && <AdminDashboard isAdmin={isAdmin} />}
        </main>
      )}
      </ModuleErrorBoundary>
      </Suspense>

      <CookieNotice onOpenPrivacy={() => navigate("/privacy")} />

      <FeedbackButton userId={user?.id} currentPage={activeTab} />

    </div>
  );
}
